import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * A thin progress bar fixed to the very top of the viewport that animates
 * during route transitions.
 *
 * Behaviour:
 *  1. On route change → jump to ~15 %, then quickly ease to ~80 %
 *  2. Short delay → complete to 100 %
 *  3. Fade out → reset to 0 % (hidden)
 */
export function RouteProgressBar() {
  const { pathname } = useLocation();
  const [progress, setProgress] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const prevPath = useRef(pathname);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Skip on initial mount
    if (prevPath.current === pathname) return;
    prevPath.current = pathname;

    // Clear any pending timers from a previous transition
    if (timerRef.current) clearTimeout(timerRef.current);

    // Phase 1 — jump to a small value and show
    setProgress(15);
    setOpacity(1);

    // Phase 2 — ease to ~80 %
    timerRef.current = setTimeout(() => setProgress(80), 50);

    // Phase 3 — complete to 100 %
    timerRef.current = setTimeout(() => setProgress(100), 400);

    // Phase 4 — fade out, then reset
    timerRef.current = setTimeout(() => setOpacity(0), 700);
    timerRef.current = setTimeout(() => setProgress(0), 1100);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pathname]);

  return (
    <div
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      className="fixed top-0 left-0 right-0 z-[9999] h-0.5 pointer-events-none"
    >
      <div
        className="h-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
        style={{
          width: `${progress}%`,
          opacity,
          transition:
            progress === 0
              ? "none"
              : "width 350ms cubic-bezier(0.4,0,0.2,1), opacity 300ms ease",
        }}
      />
    </div>
  );
}
